import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { runKeys, type BatchRunError, type BatchRunResult } from '@/hooks/useRuns';

/**
 * Hooks für die kundenweite Ausrollung (POST /runs/rollout-preview, /runs/rollout).
 *
 * Types bleiben lokal: OpenAPI ist noch nicht regeneriert, api.gen.ts
 * darf hier nicht angefasst werden.
 */

export interface RolloutRequestBase {
  routine_slugs: string[];
  customer_slugs?: string[] | null;
  exclude_customer_slugs?: string[];
  exclude_system_ids?: string[];
}

export interface RolloutRequest extends RolloutRequestBase {
  check_mode: boolean;
  snapshot: boolean;
  snapshot_override?: boolean;
  snapshot_waiver_reason?: string;
  snapshot_waiver_evidence?: string;
  idempotency_key?: string;
}

export interface RolloutPreviewGroup {
  routine_slug: string;
  display_name: string;
  execution_order: number;
  tier: string;
  system_ids: string[];
  hostnames: string[];
}

export interface RolloutPreviewExcluded {
  system_id: string;
  hostname: string;
  reason: string;
}

export interface RolloutPreviewCustomer {
  position: number;
  customer_id: string;
  slug: string;
  name: string;
  groups: RolloutPreviewGroup[];
  excluded: RolloutPreviewExcluded[];
  skipped_reason?: string | null;
}

export interface RolloutTotals {
  customers: number;
  customers_with_runs: number;
  runs: number;
  systems: number;
}

export interface RolloutPreviewResponse {
  customers: RolloutPreviewCustomer[];
  totals: RolloutTotals;
}

export interface RolloutCustomerResult {
  position: number;
  customer_id: string;
  slug: string;
  name: string;
  runs: BatchRunResult[];
  errors: BatchRunError[];
  skipped_reason?: string | null;
}

export interface RolloutResponse {
  customers: RolloutCustomerResult[];
  totals: RolloutTotals;
}

export interface RolloutPreviewInput {
  routineSlugs: string[];
  /** null = alle Kunden (alphabetisch serverseitig). */
  customerSlugs?: string[] | null;
  excludeCustomerSlugs?: string[];
  excludeSystemIds?: string[];
}

export interface StartRolloutInput extends RolloutPreviewInput {
  checkMode: boolean;
  snapshot: boolean;
  snapshotOverride?: boolean;
  snapshotWaiverReason?: string;
  snapshotWaiverEvidence?: string;
  idempotencyKey?: string;
}

function toBaseBody(input: RolloutPreviewInput): RolloutRequestBase {
  const body: RolloutRequestBase = { routine_slugs: input.routineSlugs };
  // null ist ein eigener Vertrag ("alle Kunden"), undefined heißt "nicht senden".
  if (input.customerSlugs !== undefined) body.customer_slugs = input.customerSlugs;
  if (input.excludeCustomerSlugs && input.excludeCustomerSlugs.length > 0) {
    body.exclude_customer_slugs = input.excludeCustomerSlugs;
  }
  if (input.excludeSystemIds && input.excludeSystemIds.length > 0) {
    body.exclude_system_ids = input.excludeSystemIds;
  }
  return body;
}

export function useRolloutPreview(): ReturnType<
  typeof useMutation<RolloutPreviewResponse, Error, RolloutPreviewInput>
> {
  return useMutation<RolloutPreviewResponse, Error, RolloutPreviewInput>({
    mutationFn: (input) =>
      api.post<RolloutPreviewResponse, RolloutRequestBase>(
        '/runs/rollout-preview',
        toBaseBody(input)
      ),
  });
}

export function useStartRollout(): ReturnType<
  typeof useMutation<RolloutResponse, Error, StartRolloutInput>
> {
  const qc = useQueryClient();
  return useMutation<RolloutResponse, Error, StartRolloutInput>({
    mutationFn: ({
      checkMode,
      snapshot,
      snapshotOverride,
      snapshotWaiverReason,
      snapshotWaiverEvidence,
      idempotencyKey,
      ...base
    }) => {
      const body: RolloutRequest = {
        ...toBaseBody(base),
        check_mode: checkMode,
        snapshot,
      };
      if (snapshotOverride) body.snapshot_override = true;
      if (snapshotWaiverReason) body.snapshot_waiver_reason = snapshotWaiverReason;
      if (snapshotWaiverEvidence) body.snapshot_waiver_evidence = snapshotWaiverEvidence;
      if (idempotencyKey) body.idempotency_key = idempotencyKey;
      return api.post<RolloutResponse, RolloutRequest>('/runs/rollout', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: runKeys.all });
    },
  });
}
